import { useEffect, useMemo, useState } from 'react'
import { useParams, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
    useComplianceRules,
    useDeleteComplianceRule,
    useProposeRule,
    useResetRuleTuning,
    useUpdateComplianceRule,
    type ComplianceRule,
} from '../../hooks/useComplianceApi'
import { useAsset, useAssetTypes } from '../../hooks/useQueries'
import {
    TIMES_PER_YEAR,
    conditionalClauses,
    filterRules,
    filtersFromSearch,
    filtersToSearch,
    recurrenceOf,
    timesPerYear,
    tuningSummary,
    type RuleFilters,
} from './rule-catalog'
import { RulesFilterBar } from './RulesFilterBar'
import { RuleCreateDialog } from './RuleCreateDialog'
import { RuleCloneDialog } from './RuleCloneDialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { assetTypeLabel } from '../equipment/add-equipment'
import {
    applyNumericFields,
    groupRuleBlocks,
    numericFields,
    ruleScope,
    type FamilyAssetType,
    type NumericField,
} from './rule-families'
import { RuleFamilyBlock } from './RuleFamilyBlock'
import { PageHeader } from '../shared/PageHeader'
import { RetentionRulesSection } from '../data-protection/RetentionRulesSection'
import { RecoverableChargesSection } from './RecoverableChargesSection'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import {
    mdiBookPlusOutline,
    mdiCheck,
    mdiClose,
    mdiContentCopy,
    mdiDelete,
    mdiLoading,
    mdiPencil,
    mdiPlus,
    mdiSitemapOutline,
    mdiPower,
    mdiRestore,
    mdiScaleBalance,
} from '@mdi/js'

/** Reads the editable numeric threshold out of a rule. A rule's condition is
 * no longer just a duration: it may be a windowed count, an overshoot factor
 * (divergence/drift), a warn window in days, a tolerance, a deadline, etc.
 * `readThreshold` finds the one scalar this rule exposes for inline editing
 * and returns which field it came from so `save` knows what to PATCH. `unit`
 * drives how the value is rendered (× factor, days, hours, months, meters). */
type Threshold =
    | { field: 'aggregate_value'; value: number; window?: number; op?: string; unit?: string }
    | { field: 'check_value'; value: number; window?: number; op?: string; unit?: string }
    | { field: 'warn_window_days'; value: number }
    | { field: 'frequency_months'; value: number }
    | null

/** Reads the rule's string-list parameters — the jurisdiction axis (spec
 * 2026-07-29-05, Lot 2): the ontology codes a detector keys on (contract types,
 * asset types, excluded stakeholder roles) now live on `check.params` instead of
 * being frozen in Go. Rendered read-only here: the payoff is seed-level /
 * jurisdiction-level configurability, not per-manager editing, so there is
 * deliberately no inline edit affordance for them. */
function readListParams(rule: ComplianceRule): { key: string; values: string[] }[] {
    const check = rule.check as Record<string, unknown> | undefined
    const params = check?.params as Record<string, unknown> | undefined
    if (!params || typeof params !== 'object') return []
    return Object.entries(params)
        .filter(([, v]) => Array.isArray(v) && v.length > 0 && v.every(x => typeof x === 'string'))
        .map(([key, v]) => ({ key, values: v as string[] }))
        .sort((a, b) => a.key.localeCompare(b.key))
}

function readThreshold(rule: ComplianceRule): Threshold {
    const check = rule.check as Record<string, unknown> | undefined
    // Legacy asset-scoped count norm (ECO_* recurrence / over-servicing).
    if (check && check.aggregate === 'count_over_window' && typeof check.value === 'number') {
        return {
            field: 'aggregate_value',
            value: check.value,
            window: typeof check.window_months === 'number' ? check.window_months : undefined,
            op: typeof check.op === 'string' ? check.op : undefined,
        }
    }
    // Sweep-managed detectors (spec 2026-07-20-01) carry their editable scalar
    // either as check.value (factor / count / days / tolerance) or, for pure
    // expiry rules, as warn_window_days. Some (existence, duplicate, bounds,
    // overpayment) expose no scalar — only enabled/severity are tunable.
    if (check && check.producer === 'sweep') {
        if (typeof check.value === 'number') {
            return {
                field: 'check_value',
                value: check.value,
                window: typeof check.window_months === 'number' ? check.window_months : undefined,
                op: typeof check.op === 'string' ? check.op : undefined,
                unit: typeof check.unit === 'string'
                    ? check.unit
                    : check.kind === 'ratio' || check.kind === 'drift'
                        ? 'factor'
                        : undefined,
            }
        }
        if (typeof rule.warn_window_days === 'number') {
            return { field: 'warn_window_days', value: rule.warn_window_days }
        }
        return null
    }
    if (typeof rule.frequency_months === 'number') {
        return { field: 'frequency_months', value: rule.frequency_months }
    }
    return null
}

/** A short, human summary of the condition kind, so the manager sees the rule
 * is more than a duration. Rendered as a muted secondary line. */
function describeCheck(
    rule: ComplianceRule,
    t: ReturnType<typeof useTranslation>['t'],
): string | null {
    const check = rule.check as Record<string, unknown> | undefined
    if (!check) return null
    const kind = (check.kind as string) || (check.aggregate ? 'aggregate' : undefined)
    if (!kind) return null
    const w = typeof check.window_months === 'number' ? check.window_months : undefined
    const src = check.source as string | undefined
    switch (kind) {
        case 'ratio':
            return t('business_rules.cond.ratio', { defaultValue: 'Σ {{src}} / {{w}} mois vs contracté × facteur', src: src || 'dépenses', w: w ?? 12 })
        case 'drift':
            return t('business_rules.cond.drift', { defaultValue: 'tendance sur {{w}} mois × facteur (dérive)', w: w ?? 6 })
        case 'aggregate':
        case 'count_over_window':
            return t('business_rules.cond.aggregate', { defaultValue: 'compte {{src}} / {{w}} mois', src: src || 'interventions', w: w ?? 12 })
        case 'sla_cadence':
            return t('business_rules.cond.sla', { defaultValue: 'visites constatées vs promises au contrat' })
        case 'expiry':
            return t('business_rules.cond.expiry', { defaultValue: 'échéance sous fenêtre d’alerte' })
        case 'existence':
            return t('business_rules.cond.existence', { defaultValue: 'présence/absence d’une entité liée' })
        case 'invariant':
            return t('business_rules.cond.invariant', { defaultValue: 'invariant référentiel (tolérance)' })
        case 'bounds':
            return t('business_rules.cond.bounds', { defaultValue: 'bornes plausibles (par propriété)' })
        case 'cross_source':
            return t('business_rules.cond.cross', { defaultValue: 'contradiction inter-sources' })
        case 'confidence':
            return t('business_rules.cond.confidence', { defaultValue: 'confiance d’extraction sous seuil' })
        case 'coverage_cascade':
            return t('business_rules.cond.coverage', { defaultValue: 'cascade de garanties (GPA/biennale/décennale)' })
        case 'claim_deadline':
            return t('business_rules.cond.claim', { defaultValue: 'délai de position de l’assureur (jours)' })
        case 'staleness':
            return t('business_rules.cond.staleness', { defaultValue: 'ancienneté depuis le dépôt' })
        case 'duplicate':
            return t('business_rules.cond.duplicate', { defaultValue: 'empreinte de fichier identique' })
        case 'arrears':
            return t('business_rules.cond.arrears', { defaultValue: 'retard de paiement (jours)' })
        default:
            return null
    }
}

const OP_LABEL: Record<string, string> = { lte: '≤', lt: '<', gte: '≥', gt: '>', eq: '=' }

function localized(m: Record<string, string> | undefined, lang: string): string {
    if (!m) return ''
    return m[lang] || m.fr || m.en || Object.values(m)[0] || ''
}

const SEVERITY_TONE: Record<number, string> = {
    1: 'text-red-600 dark:text-red-400',
    2: 'text-orange-600 dark:text-orange-400',
    3: 'text-amber-600 dark:text-amber-400',
    4: 'text-sky-600 dark:text-sky-400',
    5: 'text-muted-foreground',
}

export function BusinessRulesPage() {
    const { org } = useParams({ strict: false }) as { org: string }
    const { t, i18n } = useTranslation('compliance')
    const lang = i18n.language

    // Same trail as every other organization-settings destination
    // (NormalizationsPage, DerivationsPage, AggregationsPage, TagsManagementPage):
    // Home > Settings > <page>. Business rules are reached from
    // OrganizationSettingsPage's "compliance" group, so the settings hop has to
    // be there for the breadcrumb to walk back the way the user came in.
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('common:nav.settings'), path: `/${org}/settings` },
        { title: t('business_rules.title', { defaultValue: 'Règles' }) },
    ])

    const { data: rules = [], isLoading } = useComplianceRules(org)
    const { data: assetTypes = [] } = useAssetTypes(org)
    const types = assetTypes as unknown as (FamilyAssetType & { name?: Record<string, string> | null })[]
    // Creating a rule is a compliance write, the permission the server checks.
    const canWrite = useCan('compliance.write')
    const [creating, setCreating] = useState<{ controlUid?: string } | null>(null)

    // The filters live in the URL (spec 2026-10-27-21 § 3), so a link reopens
    // the same view. Filtering happens here, on the full list.
    const search = useSearch({ strict: false }) as Record<string, unknown>
    const navigate = useNavigate()
    const location = useLocation()
    const filters = useMemo(() => filtersFromSearch(search), [search])
    const setFilters = (next: RuleFilters) => {
        navigate({
            to: '/$org/business-rules',
            params: { org },
            search: filtersToSearch(next),
            hash: (location.hash || '').replace(/^#/, '') || undefined,
            replace: true,
        })
    }
    const visible = useMemo(() => filterRules(rules, filters), [rules, filters])

    // Group rules by their control's localized name — the manager-facing domain.
    // `code` is the stable control code (e.g. INSURANCE) used to deep-link from a
    // ticket's "Règle" box (spec 2026-07-18-01). A group a filter empties goes.
    const groups = useMemo(() => {
        const byControl = new Map<string, { title: string; code?: string; uid?: string; rules: ComplianceRule[] }>()
        for (const r of visible) {
            const ctrl = r.compliance_control
            const key = ctrl?.uid ?? r.compliance_control_uid ?? 'autres'
            const title = ctrl ? localized(ctrl.name, lang) : t('business_rules.ungrouped', { defaultValue: 'Autres' })
            if (!byControl.has(key)) byControl.set(key, { title, code: ctrl?.code, uid: ctrl?.uid ?? r.compliance_control_uid, rules: [] })
            byControl.get(key)!.rules.push(r)
        }
        return [...byControl.values()].sort((a, b) => a.title.localeCompare(b.title))
    }, [visible, lang, t])

    // Deep-link target: a ticket links here with #rule-<CODE> (the rule that
    // raised it, a family member included) or #control-<CODE>. Once the rules
    // have loaded, scroll the match into view and flash it.
    const [highlight, setHighlight] = useState<string | null>(null)
    useEffect(() => {
        // TanStack Router's location.hash omits the leading '#'; a raw
        // window.location.hash keeps it. Tolerate both.
        const raw = (location.hash || '').replace(/^#/, '')
        if (!/^(control|rule)-.+$/.test(raw) || isLoading) return
        const el = document.getElementById(raw)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setHighlight(raw)
        const timer = setTimeout(() => setHighlight(null), 2500)
        return () => clearTimeout(timer)
    }, [location.hash, isLoading, groups])

    const kindTitle = filters.klass === 'all'
        ? t('business_rules.title', { defaultValue: 'Règles' })
        : `${t('business_rules.title', { defaultValue: 'Règles' })} · ${t(`business_rules.class.${filters.klass}`)}`

    return (
        <div data-testid="business-rules-page">
            <PageHeader
                className="mb-5"
                icon={SECTION_ICONS.businessRules}
                title={kindTitle}
                description={t('business_rules.subtitle', { defaultValue: 'Les normes contre lesquelles les tickets sont évalués. Ajustez un seuil et le moteur s’aligne à la prochaine évaluation.' })}
                actions={
                    <div className="flex flex-wrap gap-2">
                        {/* Remediation scenarios live beside the rules (spec 2026-10-28-15 § 10). */}
                        <Button variant="outline" onClick={() => navigate({ to: '/$org/remediation-scenarios', params: { org } })} data-testid="business-rules-scenarios">
                            <AppIcon path={mdiSitemapOutline} size="sm" />
                            {t('scenarios:open_list')}
                        </Button>
                        {canWrite && (
                            <Button onClick={() => setCreating({})} data-testid="business-rules-new">
                                <AppIcon path={mdiPlus} size="sm" />
                                {t('business_rules.new', { defaultValue: 'Nouvelle règle' })}
                            </Button>
                        )}
                    </div>
                }
            />

            <RulesFilterBar filters={filters} onChange={setFilters} />

            {creating && (
                <RuleCreateDialog org={org} types={types} defaultControlUid={creating.controlUid} onClose={() => setCreating(null)} />
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
                </div>
            ) : rules.length === 0 ? (
                <EmptyState
                    icon={mdiScaleBalance}
                    title={t('business_rules.empty', { defaultValue: 'Aucune règle de gestion définie.' })}
                    data-testid="business-rules-empty"
                />
            ) : groups.length === 0 ? (
                <EmptyState
                    icon={mdiScaleBalance}
                    title={t('business_rules.no_match', { defaultValue: 'Aucune règle ne correspond à ces filtres.' })}
                    data-testid="business-rules-no-match"
                />
            ) : (
                <div className="space-y-6">
                    {groups.map((g) => (
                        <section
                            key={g.title}
                            id={g.code ? `control-${g.code}` : undefined}
                            className="scroll-mt-4 rounded-lg transition-shadow"
                            data-testid={`business-rules-group-${g.title}`}
                            data-control-code={g.code}
                        >
                            <div className="mb-2 flex items-center gap-2">
                                <h2 className="flex-1 text-sm font-semibold text-muted-foreground uppercase tracking-wide">{g.title}</h2>
                                {canWrite && g.uid && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 text-xs"
                                        onClick={() => setCreating({ controlUid: g.uid })}
                                        data-testid={`business-rules-group-new-${g.code ?? g.uid}`}
                                    >
                                        <AppIcon path={mdiPlus} size="xs" />
                                        {t('business_rules.new', { defaultValue: 'Nouvelle règle' })}
                                    </Button>
                                )}
                            </div>
                            <div
                                className={`border rounded-lg divide-y transition-all ${
                                    g.code && highlight === `control-${g.code}` ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                                }`}
                            >
                                {groupRuleBlocks(g.rules, types).map((b) =>
                                    b.kind === 'rule' ? (
                                        <RuleRow key={b.rule.uid} org={org} rule={b.rule} lang={lang} t={t} types={types} highlight={highlight} />
                                    ) : (
                                        <RuleFamilyBlock key={b.family} org={org} family={b.family} members={b.members}>
                                            {b.members.map((m) => (
                                                <RuleRow key={m.uid} org={org} rule={m} lang={lang} t={t} types={types} highlight={highlight} />
                                            ))}
                                        </RuleFamilyBlock>
                                    ),
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {/* Charges récupérables (spec 2026-10-25-09). */}
            <RecoverableChargesSection org={org} />

            {/* Conservation des données (spec 2026-10-03-02, lot 3). */}
            <RetentionRulesSection org={org} />
        </div>
    )
}

/** Unit suffix of an editable condition field (spec 2026-10-27-18). */
const FIELD_UNIT: Record<string, 'months' | 'days' | 'celsius' | 'factor'> = {
    window_months: 'months',
    grace_months: 'months',
    baseline_months: 'months',
    horizon_months: 'months',
    frequency_months: 'months',
    max_gap_days: 'days',
    lead_days: 'days',
    grace_days: 'days',
    warn_window_days: 'days',
    heating_base_c: 'celsius',
    heating_threshold_c: 'celsius',
    cooling_base_c: 'celsius',
    escalate_factor: 'factor',
}

/** Every number of a rule a manager edits: the condition's numbers, else the
 * rule-level warn window or frequency. */
function editableFields(rule: ComplianceRule): NumericField[] {
    const fields = numericFields(rule.check as Record<string, unknown>)
    if (fields.length > 0) return fields
    const threshold = readThreshold(rule)
    if (threshold?.field === 'warn_window_days' || threshold?.field === 'frequency_months') {
        return [{ key: threshold.field, scope: 'check', value: threshold.value }]
    }
    return []
}

function RuleScopeChip({
    rule,
    types,
    lang,
    t,
}: {
    rule: ComplianceRule
    types: (FamilyAssetType & { name?: Record<string, string> | null })[]
    lang: string
    t: ReturnType<typeof useTranslation>['t']
}) {
    // A member scoped to buildings or sites (spec 2026-10-27-21 § 5) names them.
    if (rule.scope_asset_uids && rule.scope_asset_uids.length > 0) {
        return (
            <span className="inline-flex flex-shrink-0 items-center gap-1" data-testid={`rule-scope-${rule.uid}`}>
                {rule.scope_asset_uids.map((uid) => <ScopeAssetChip key={uid} uid={uid} />)}
            </span>
        )
    }
    const scope = ruleScope(rule, types)
    const label = scope.kind === 'all_assets'
        ? t('business_rules.scope.all_assets', { defaultValue: 'Tous les actifs' })
        : scope.kind === 'all_equipment'
            ? t('business_rules.scope.all_equipment', { defaultValue: 'Tous les équipements' })
            : scope.type
                ? assetTypeLabel(scope.type, lang)
                : t('business_rules.scope.unknown_type', { defaultValue: 'Type inconnu' })
    return (
        <span
            className="inline-flex flex-shrink-0 items-center rounded-full border bg-muted/50 px-2 py-px text-[11px] text-foreground/80"
            data-testid={`rule-scope-${rule.uid}`}
        >
            {label}
        </span>
    )
}

/** The name of a building or site a family member is scoped to. */
function ScopeAssetChip({ uid }: { uid: string }) {
    const { org } = useParams({ strict: false }) as { org: string }
    const { data: asset } = useAsset(org, uid)
    const name = (asset as { name?: string } | undefined)?.name
    return (
        <span className="inline-flex items-center rounded-full border border-primary/40 bg-primary/5 px-2 py-px text-[11px] text-foreground/80">
            {name || uid.slice(0, 8)}
        </span>
    )
}

function RuleRow({
    org,
    rule,
    lang,
    t,
    types,
    highlight,
}: {
    org: string
    rule: ComplianceRule
    lang: string
    t: ReturnType<typeof useTranslation>['t']
    types: (FamilyAssetType & { name?: Record<string, string> | null })[]
    highlight?: string | null
}) {
    const threshold = readThreshold(rule)
    const recurrence = recurrenceOf(rule)
    const fields = recurrence !== null ? [] : editableFields(rule)
    const [editing, setEditing] = useState(false)
    const [cloning, setCloning] = useState(false)
    const [drafts, setDrafts] = useState<Record<string, string>>({})
    const [saveError, setSaveError] = useState<string | null>(null)
    const update = useUpdateComplianceRule(org, rule.uid)
    const remove = useDeleteComplianceRule(org)
    const reset = useResetRuleTuning(org, rule.uid)
    const propose = useProposeRule(org, rule.uid)
    // Every write here goes through the compliance rule routes, which check
    // `compliance.write` (spec 2026-10-27-21 § 3). A read-only caller keeps the
    // whole list — values included — but no write affordance.
    const canWrite = useCan('compliance.write')
    const isOrgRule = rule.origin === 'organization' || (rule.code ?? '').startsWith('ORG_')
    // An organization's rule can be deleted; a shipped one can only be disabled.
    const canDelete = canWrite && isOrgRule
    const tuned = !isOrgRule ? tuningSummary(rule) : []
    const editable = recurrence !== null || fields.length > 0

    const openEditor = (open: boolean) => {
        if (!canWrite || !editable) return
        if (open) {
            setSaveError(null)
            setDrafts(recurrence !== null
                ? { recurrence: String(recurrence), warn: String(rule.warn_window_days ?? '') }
                : Object.fromEntries(fields.map((f) => [`${f.scope}.${f.key}`, String(f.value)])))
        }
        setEditing(open)
    }

    const save = () => {
        if (!canWrite) return
        const done = {
            onSuccess: () => setEditing(false),
            onError: (e: unknown) => {
                const err = e as { code?: string; source_ref?: string; title?: string }
                setSaveError(err.code === 'RULE_LOOSER_THAN_REGULATION'
                    ? t('business_rules.recurrence.looser', { source: err.source_ref ?? '' })
                    : err.title ?? t('business_rules.save_error', { defaultValue: 'Enregistrement impossible.' }))
            },
        }
        if (recurrence !== null) {
            const months = Number(drafts.recurrence)
            const warn = drafts.warn?.trim() ? Number(drafts.warn) : undefined
            const body: { recurrence_months?: number; warn_window_days?: number } = {}
            if (Number.isFinite(months) && months > 0 && months !== recurrence) body.recurrence_months = months
            if (warn !== undefined && Number.isFinite(warn) && warn !== rule.warn_window_days) body.warn_window_days = warn
            if (Object.keys(body).length === 0) { setEditing(false); return }
            update.mutate(body, done)
            return
        }
        const edits: Record<string, number> = {}
        for (const [k, v] of Object.entries(drafts)) {
            const n = Number(v)
            if (v.trim() !== '' && Number.isFinite(n)) edits[k] = n
        }
        if (fields[0]?.key === 'warn_window_days' && fields.length === 1 && edits['check.warn_window_days'] !== undefined) {
            update.mutate({ warn_window_days: edits['check.warn_window_days'] }, done)
        } else if (fields[0]?.key === 'frequency_months' && fields.length === 1 && edits['check.frequency_months'] !== undefined) {
            update.mutate({ frequency_months: edits['check.frequency_months'] }, done)
        } else {
            const nextCheck = applyNumericFields(rule.check as Record<string, unknown>, edits, fields)
            update.mutate({ check: nextCheck }, done)
        }
    }

    const fieldUnit = (key: string): string => {
        switch (FIELD_UNIT[key]) {
            case 'months': return t('business_rules.months', { defaultValue: 'mois' })
            case 'days': return t('business_rules.unit.days', { defaultValue: 'j' })
            case 'celsius': return '°C'
            case 'factor': return '×'
            default: return ''
        }
    }

    const unitLabel = (unit?: string): string => {
        switch (unit) {
            case 'days': return ' ' + t('business_rules.unit.days', { defaultValue: 'j' })
            case 'hours': return ' ' + t('business_rules.unit.hours', { defaultValue: 'h' })
            case 'meters': return ' ' + t('business_rules.unit.meters', { defaultValue: 'm' })
            default: return ''
        }
    }

    const conditionText = describeCheck(rule, t)
    const listParams = readListParams(rule)

    // A periodic rule reads as a cadence (spec 2026-10-27-21 § 3): "2 fois par
    // an" or "tous les 18 mois", with its warning window.
    const perYear = recurrence !== null ? timesPerYear(recurrence) : null
    const recurrenceText = recurrence === null
        ? null
        : perYear
            ? t('business_rules.recurrence.times_per_year', { count: perYear })
            : t('business_rules.every_months', { defaultValue: 'tous les {{n}} mois', n: recurrence })
    const thresholdText = recurrenceText
        ? `${recurrenceText}${typeof rule.warn_window_days === 'number' ? ` · ${t('business_rules.recurrence.warn_short', { n: rule.warn_window_days })}` : ''}`
        : !threshold
        ? t('business_rules.no_threshold', { defaultValue: '—' })
        : threshold.field === 'frequency_months'
            ? t('business_rules.every_months', { defaultValue: 'tous les {{n}} mois', n: threshold.value })
            : threshold.field === 'warn_window_days'
                ? t('business_rules.warn_days', { defaultValue: '{{n}} j avant', n: threshold.value })
                : threshold.field === 'aggregate_value'
                    ? `${threshold.op ? OP_LABEL[threshold.op] ?? threshold.op : ''} ${threshold.value}${threshold.window ? ` / ${threshold.window} ${t('business_rules.months', { defaultValue: 'mois' })}` : ''}`.trim()
                    // check_value: factor (×1.25) or op-prefixed scalar with unit
                    : threshold.unit === 'factor'
                        ? `× ${threshold.value}`
                        : `${threshold.op ? OP_LABEL[threshold.op] ?? threshold.op : ''} ${threshold.value}${unitLabel(threshold.unit)}`.trim()

    const toggleEnabled = () => {
        if (!canWrite) return
        update.mutate({ enabled: !rule.enabled })
    }

    const enabledTone = rule.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
    const enabledLabel = rule.enabled
        ? t('business_rules.status_enabled', { defaultValue: 'Règle active' })
        : t('business_rules.status_disabled', { defaultValue: 'Règle inactive' })

    const rowId = rule.code ? `rule-${rule.code}` : undefined
    const clauses = conditionalClauses(rule)

    return (
        <div
            id={rowId}
            className={`flex scroll-mt-24 items-center gap-3 px-4 py-2.5 transition-all ${rule.enabled ? '' : 'opacity-60'} ${
                rowId && highlight === rowId ? 'bg-primary/5 ring-2 ring-inset ring-primary' : ''
            }`}
            data-testid={`rule-row-${rule.uid}`}
            data-rule-code={rule.code}
        >
            <div className={`w-1.5 h-6 rounded-full flex-shrink-0 ${rule.enabled ? (SEVERITY_TONE[rule.severity]?.replace('text-', 'bg-') ?? 'bg-muted') : 'bg-muted'}`} title={`sévérité ${rule.severity}`} />
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate">{localized(rule.label, lang) || rule.code}</span>
                    <RuleScopeChip rule={rule} types={types} lang={lang} t={t} />
                    {rule.jurisdiction && rule.jurisdiction !== '*' && (
                        <span className="inline-flex flex-shrink-0 items-center rounded border px-1.5 py-px font-mono text-[11px] text-muted-foreground" data-testid={`rule-jurisdiction-${rule.uid}`}>
                            {rule.jurisdiction}
                        </span>
                    )}
                    {(rule.universes ?? []).length > 0 && (
                        <span className="inline-flex flex-shrink-0 items-center gap-1" data-testid={`rule-universes-${rule.uid}`}>
                            {(rule.universes ?? []).map((u) => (
                                <span key={u} className="rounded-full border border-info/25 bg-info/10 px-2 py-px text-[11px] text-info">
                                    {t(`business_rules.universe.${u}`)}
                                </span>
                            ))}
                        </span>
                    )}
                    {isOrgRule && (
                        <span className="rounded-full border border-primary/25 bg-primary/10 px-2 py-px text-[11px] text-primary" data-testid={`rule-origin-${rule.uid}`}>
                            {t('business_rules.badge.organization')}
                        </span>
                    )}
                    {rule.proposed_at && (
                        <span className="rounded-full border px-2 py-px text-[11px] text-muted-foreground" data-testid={`rule-proposed-${rule.uid}`}>
                            {t('business_rules.badge.proposed')}
                        </span>
                    )}
                    {tuned.length > 0 && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help rounded-full border border-warning/25 bg-warning/10 px-2 py-px text-[11px] text-warning" data-testid={`rule-tuned-${rule.uid}`}>
                                    {t('business_rules.badge.tuned')}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <div className="text-xs">
                                    <div className="font-medium">{t('business_rules.badge.tuned_tooltip')}</div>
                                    {tuned.map((line) => <div key={line} className="font-mono">{line}</div>)}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                    {conditionText && <span className="text-[11px] text-muted-foreground truncate">{conditionText}</span>}
                    {rule.code && <span className="text-[11px] text-muted-foreground/70 font-mono truncate">{rule.code}</span>}
                </div>
                {listParams.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5" data-testid={`rule-list-params-${rule.uid}`}>
                        {listParams.map(param => (
                            <span key={param.key} className="text-[11px] text-muted-foreground/70">
                                <span className="font-mono">{param.key}</span>
                                {': '}
                                {param.values.map(value => (
                                    <span
                                        key={value}
                                        className="ml-1 inline-block rounded bg-muted px-1 py-px font-mono text-foreground/80"
                                    >
                                        {value}
                                    </span>
                                ))}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            {canWrite ? (
                <Button
                    size="icon"
                    variant="ghost"
                    className={`h-8 w-8 ${enabledTone}`}
                    onClick={toggleEnabled}
                    disabled={update.isPending}
                    aria-label={rule.enabled ? t('business_rules.disable', { defaultValue: 'Désactiver la règle' }) : t('business_rules.enable', { defaultValue: 'Activer la règle' })}
                    title={rule.enabled ? t('business_rules.disable', { defaultValue: 'Désactiver la règle' }) : t('business_rules.enable', { defaultValue: 'Activer la règle' })}
                    data-testid={`rule-toggle-${rule.uid}`}
                >
                    <AppIcon path={mdiPower} size="sm" />
                </Button>
            ) : (
                // The on/off state is a value, not an action: it stays on
                // screen as a plain indicator when the toggle is hidden.
                <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center ${enabledTone}`}
                    aria-label={enabledLabel}
                    title={enabledLabel}
                    data-testid={`rule-status-${rule.uid}`}
                >
                    <AppIcon path={mdiPower} size="sm" />
                </span>
            )}
            <div className="flex items-center gap-1 flex-shrink-0">
                <span className="mr-1 text-sm font-medium text-foreground tabular-nums" data-testid={`rule-threshold-${rule.uid}`}>{thresholdText}</span>
                {canWrite && editable && (
                    <Popover open={editing} onOpenChange={openEditor}>
                        <PopoverTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label={t('business_rules.edit', { defaultValue: 'Modifier le seuil' })} title={t('business_rules.edit', { defaultValue: 'Modifier le seuil' })} data-testid={`rule-edit-${rule.uid}`}>
                                <AppIcon path={mdiPencil} size="xs" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-80" data-testid={`rule-editor-${rule.uid}`}>
                            <form
                                className="space-y-2"
                                onSubmit={(e) => { e.preventDefault(); save() }}
                            >
                                {recurrence !== null ? (
                                    <>
                                        <label className="flex items-center gap-2 text-sm">
                                            <span className="flex-1 text-muted-foreground">{t('business_rules.recurrence.label')}</span>
                                            <select
                                                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                                                value={timesPerYear(Number(drafts.recurrence)) ? String(12 / Number(drafts.recurrence)) : 'custom'}
                                                onChange={(e) => {
                                                    const v = e.target.value
                                                    if (v !== 'custom') setDrafts((d) => ({ ...d, recurrence: String(12 / Number(v)) }))
                                                }}
                                                data-testid={`rule-recurrence-per-year-${rule.uid}`}
                                            >
                                                {TIMES_PER_YEAR.map((x) => (
                                                    <option key={x} value={x}>{t('business_rules.recurrence.times_per_year', { count: x })}</option>
                                                ))}
                                                <option value="custom">{t('business_rules.recurrence.custom')}</option>
                                            </select>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <span className="flex-1 text-muted-foreground">{t('business_rules.recurrence.every_months')}</span>
                                            <Input
                                                type="number"
                                                min={1}
                                                value={drafts.recurrence ?? ''}
                                                onChange={(e) => setDrafts((d) => ({ ...d, recurrence: e.target.value }))}
                                                className="h-8 w-20"
                                                data-testid={`rule-recurrence-months-${rule.uid}`}
                                            />
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <span className="flex-1 text-muted-foreground">{t('business_rules.recurrence.warn')}</span>
                                            <Input
                                                type="number"
                                                min={0}
                                                value={drafts.warn ?? ''}
                                                onChange={(e) => setDrafts((d) => ({ ...d, warn: e.target.value }))}
                                                className="h-8 w-20"
                                                data-testid={`rule-warn-days-${rule.uid}`}
                                            />
                                        </label>
                                        {clauses.length > 0 && (
                                            <div className="rounded bg-muted/50 p-2 text-[11px] text-muted-foreground" data-testid={`rule-clauses-${rule.uid}`}>
                                                <div className="font-medium">{t('business_rules.recurrence.clauses')}</div>
                                                {clauses.map((c, i) => (
                                                    <div key={i} className="font-mono">
                                                        {`${String((c.when as { property?: string }).property ?? '')} ${String((c.when as { op?: string }).op ?? '')} ${String((c.when as { value?: unknown }).value ?? '')} → ${c.months} ${t('business_rules.months', { defaultValue: 'mois' })}`}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : fields.map((f) => {
                                    const id = `${f.scope}.${f.key}`
                                    const unit = fieldUnit(f.key)
                                    return (
                                        <label key={id} className="flex items-center gap-2 text-sm">
                                            <span className="flex-1 text-muted-foreground">
                                                {t(`business_rules.field.${f.key}`, { defaultValue: f.key })}
                                            </span>
                                            <Input
                                                type="number"
                                                step="any"
                                                value={drafts[id] ?? ''}
                                                onChange={(e) => setDrafts((d) => ({ ...d, [id]: e.target.value }))}
                                                className="h-8 w-20"
                                                data-testid={`rule-field-${rule.uid}-${f.key}`}
                                            />
                                            <span className="w-8 text-xs text-muted-foreground">{unit}</span>
                                        </label>
                                    )
                                })}
                                {saveError && <p className="text-xs text-destructive" data-testid={`rule-save-error-${rule.uid}`}>{saveError}</p>}
                                <div className="flex justify-end gap-2 pt-1">
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                                        <AppIcon path={mdiClose} size="sm" />
                                        {t('business_rules.cancel', { defaultValue: 'Annuler' })}
                                    </Button>
                                    <Button type="submit" size="sm" disabled={update.isPending} data-testid={`rule-save-${rule.uid}`}>
                                        {update.isPending ? <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" /> : <AppIcon path={mdiCheck} size="sm" />}
                                        {t('business_rules.save', { defaultValue: 'Enregistrer' })}
                                    </Button>
                                </div>
                            </form>
                        </PopoverContent>
                    </Popover>
                )}
                {canWrite && tuned.length > 0 && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => reset.mutate(undefined)}
                        disabled={reset.isPending}
                        aria-label={t('business_rules.reset')}
                        title={t('business_rules.reset')}
                        data-testid={`rule-reset-${rule.uid}`}
                    >
                        <AppIcon path={mdiRestore} size="xs" />
                    </Button>
                )}
                {canWrite && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => setCloning(true)}
                        aria-label={t('business_rules.clone.action')}
                        title={t('business_rules.clone.action')}
                        data-testid={`rule-clone-${rule.uid}`}
                    >
                        <AppIcon path={mdiContentCopy} size="xs" />
                    </Button>
                )}
                {canWrite && isOrgRule && !rule.proposed_at && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => propose.mutate()}
                        disabled={propose.isPending}
                        aria-label={t('business_rules.propose')}
                        title={t('business_rules.propose')}
                        data-testid={`rule-propose-${rule.uid}`}
                    >
                        <AppIcon path={mdiBookPlusOutline} size="xs" />
                    </Button>
                )}
                {canDelete && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => remove.mutate(rule.uid)}
                        disabled={remove.isPending}
                        aria-label={t('business_rules.delete_rule', { defaultValue: 'Supprimer la règle' })}
                        title={t('business_rules.delete_rule', { defaultValue: 'Supprimer la règle' })}
                        data-testid={`rule-delete-${rule.uid}`}
                    >
                        <AppIcon path={mdiDelete} size="xs" />
                    </Button>
                )}
            </div>
            {cloning && <RuleCloneDialog org={org} rule={rule} onClose={() => setCloning(false)} />}
        </div>
    )
}
